import { useEffect, useRef, useState, type MutableRefObject } from 'react';

type UseHeaderDarkSectionOptions = {
  targetId: string;
};

type UseHeaderDarkSectionReturn = {
  headerRef: MutableRefObject<HTMLElement | null>;
  isDarkSection: boolean;
};

export const useHeaderDarkSection = (
  options: UseHeaderDarkSectionOptions
): UseHeaderDarkSectionReturn => {
  const { targetId } = options;
  const headerRef = useRef<HTMLElement | null>(null);
  const [isDarkSection, setIsDarkSection] = useState(false);

  useEffect(() => {
    const target = document.getElementById(targetId);
    const header = headerRef.current;
    if (!target || !header) return;

    const checkOverlap = () => {
      const headerBottom = header.getBoundingClientRect().bottom;
      const rect = target.getBoundingClientRect();
      setIsDarkSection(rect.top <= headerBottom && rect.bottom > 0);
    };

    let scrollTicking = false;
    const handleScroll = () => {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(() => {
        checkOverlap();
        scrollTicking = false;
      });
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting) {
          checkOverlap();
          window.addEventListener('scroll', handleScroll, { passive: true });
        } else {
          window.removeEventListener('scroll', handleScroll);
          setIsDarkSection(false);
        }
      },
      { threshold: 0 }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [targetId]);

  return { headerRef, isDarkSection };
};

export default useHeaderDarkSection;
